<script setup lang="ts">
import { computed, useId } from "vue";
import { HPixTextField } from "@pixelpoint-pattern-library-v2/base/components/headless/base/inputs/HPixTextField/index";

type Props = {
  modelValue?: string;
  type?: "text" | "email" | "password" | "tel" | "url" | "number";
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  min?: number;
  max?: number;
  /** The native `maxlength`: the browser stops typing and pasting past this many characters. */
  maxlength?: number;
  class?: string | string[];
};

const props = withDefaults(defineProps<Props>(), {
  type: "text",
  disabled: false,
  required: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  blur: [];
}>();

const fieldId = useId();
const errorId = `${fieldId}-error`;
const hasError = computed(() => !!props.error);
</script>

<template>
  <div :class="['pix-text-field__root', props.class]">
    <label v-if="props.label" :for="fieldId" class="pix-text-field__label" :data-required="props.required || undefined">
      {{ props.label }}
    </label>
    <HPixTextField
      :id="fieldId"
      :model-value="props.modelValue"
      :type="props.type"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      :required="props.required"
      :min="props.min"
      :max="props.max"
      :maxlength="props.maxlength"
      :aria-required="props.required || undefined"
      :aria-invalid="hasError || undefined"
      :aria-describedby="hasError ? errorId : undefined"
      class="pix-text-field__input"
      :data-error="hasError || undefined"
      @update:model-value="emit('update:modelValue', $event)"
      @blur="emit('blur')"
    />
    <span v-if="hasError" :id="errorId" class="pix-text-field__error" role="alert">
      {{ props.error }}
    </span>
  </div>
</template>

<style>
@layer pix-components {
  /* Declared on __root (the wrapping <div>) so both the label and the input can read them,
     mirroring PixSelect's __field. */
  .pix-text-field__root {
    --_pix-text-field-surface: var(--pix-field-surface, var(--color-surface));
    --_pix-text-field-border-color: var(--pix-field-border-color, var(--color-surface-border));
    --_pix-text-field-border-width: var(--pix-field-border-width, var(--border-thin));

    /* Neither radius nor min-height was ever declared here, so the defaults are the CSS
       initial values: the properties are a no-op until the family is set. */
    --_pix-text-field-radius: var(--pix-field-radius, 0);
    --_pix-text-field-min-height: var(--pix-field-min-height, auto);
    --_pix-text-field-padding-block: var(--pix-field-padding-block, var(--spacing-small));
    --_pix-text-field-padding-inline: var(--pix-field-padding-inline, var(--spacing-medium));
    --_pix-text-field-shadow: var(--pix-field-shadow, var(--shadow-soft));
    --_pix-text-field-text: var(--pix-field-text, var(--color-text-on-surface));
    --_pix-text-field-placeholder: var(--pix-field-placeholder, var(--color-text-muted));
    --_pix-text-field-font-size: var(--pix-field-font-size, var(--text-base));
    --_pix-text-field-label-size: var(--pix-field-label-size, var(--text-sm));
    --_pix-text-field-label-weight: var(--pix-field-label-weight, 500);
    --_pix-text-field-label-color: var(--pix-field-label-color, var(--color-text));

    display: flex;
    flex-direction: column;
    gap: var(--spacing-xsmall);
  }

  .pix-text-field__label {
    /* No margin — this label is spaced by the root's flex `gap`. PixSelect matches; PixDatePicker
       cannot use a gap (see there) and reproduces the same distance with a margin. */
    font-size: var(--_pix-text-field-label-size);
    font-weight: var(--_pix-text-field-label-weight);
    color: var(--_pix-text-field-label-color);
  }

  .pix-text-field__label[data-required]::after {
    content: " *" / "";
    color: var(--color-error);
  }

  .pix-text-field__input {
    width: 100%;
    min-height: var(--_pix-text-field-min-height);
    padding: var(--_pix-text-field-padding-block) var(--_pix-text-field-padding-inline);
    border: var(--_pix-text-field-border-width) solid var(--_pix-text-field-border-color);
    border-radius: var(--_pix-text-field-radius);
    background: var(--_pix-text-field-surface);
    box-shadow: var(--_pix-text-field-shadow);
    outline: none;
    font-size: var(--_pix-text-field-font-size);
    color: var(--_pix-text-field-text);

    &::placeholder {
      color: var(--_pix-text-field-placeholder);
      opacity: 1; /* Firefox dims placeholders to 54% */
    }

    &:hover:not(:disabled) {
      border-color: var(--color-text-muted);
    }

    &:focus-visible {
      outline: var(--focus-ring-width) solid var(--focus-ring-color);
      outline-offset: var(--focus-ring-offset);
    }

    /* Was 0.65 here and on PixCheckbox's label while the box next to it used 0.5; the family now
       dims uniformly through the foundation token. */
    &:disabled {
      cursor: not-allowed;
      opacity: var(--opacity-disabled-state);
    }

    /* Token flip rather than a property override, so the border shorthand's width survives.
       Matches PixSelect. */
    &[data-error] {
      --_pix-text-field-border-color: var(--color-error);
    }
  }

  .pix-text-field__error {
    font-size: var(--text-sm);
    color: var(--color-error);
  }
}
</style>
